import React, { useState, useCallback, useRef } from "react"
import ReactFlow, {MiniMap,Controls,Background,useNodesState,useEdgesState,addEdge,Panel,Handle,Position,useReactFlow,ReactFlowProvider,} from "reactflow"
import "reactflow/dist/style.css"
import { motion, AnimatePresence } from "framer-motion"
import { MessageSquare, HelpCircle, GitBranch, CheckSquare, Info, RefreshCw, Repeat, X, File, FileText, Save, Undo2, Redo2, ZoomIn, ZoomOut, Maximize, Trash2,Tags, Users, Bot, MessageCircle, Clock, Webhook, Table2, Database, Package, Settings, UserCheck, FileBadge, GripVertical, Maximize2, Minimize2 } from 'lucide-react'

// Generate unique node IDs
const getNodeId = () => `node_${Math.random().toString(36).substr(2, 9)}`;

// Define node types with improved styling
const elementTypes = [
  {
    type: "Send a message",
    icon: <MessageSquare className="h-5 w-5" />,
    color: "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200",
    description: "Send a message to the user",
  },
  {
    type: "Ask a Question",
    icon: <HelpCircle className="h-5 w-5" />,
    color: "bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200",
    description: "Ask the user a question",
    subTypes: [
      {
        type: "Question",
        icon: <HelpCircle className="h-4 w-4" />,
        description: "Ask anything to user",
        questionType: "text"
      },
      {
        type: "Button",
        icon: <CheckSquare className="h-4 w-4" />,
        description: "Choice based on button\n(maximum of 3 choices)",
        questionType: "button"
      },
      {
        type: "List",
        icon: <Info className="h-4 w-4" />,
        description: "Choice based on buttons\n(maximum of 10 choices)",
        questionType: "button"
      }
    ]
  },
  {
    type: "Set a Condition",
    icon: <GitBranch className="h-5 w-5" />,
    color: "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200",
    description: "Create a conditional logic branch",
  },
  {
    type: "Subscribe",
    icon: <MessageCircle className="h-5 w-5" />,
    color: "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200",
    description: "Subscribe to bot updates",
  },
  {
    type: "Unsubscribe",
    icon: <MessageCircle className="h-5 w-5" />,
    color: "bg-gradient-to-br from-red-50 to-red-100 border-red-200",
    description: "Unsubscribe from bot updates",
  },
  {
    type: "Update Attribute",
    icon: <Settings className="h-5 w-5" />,
    color: "bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200",
    description: "Update user attributes or properties",
  },
  {    
    type: "Set Tags",
    icon: <Tags className="h-5 w-5" />,
    color: "bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200",
    description: "Add or remove tags from the conversation",
  },
  {
    type: "Assign Team",
    icon: <Users className="h-5 w-5" />,
    color: "bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200",
    description: "Assign conversation to a team",
  },
  {
    type: "Assign User",
    icon: <UserCheck className="h-5 w-5" />,
    color: "bg-gradient-to-br from-lime-50 to-lime-100 border-lime-200",
    description: "Assign conversation to a specific user",
  },
  {
    type: "Trigger Chatbot",
    icon: <Bot className="h-5 w-5" />,
    color: "bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 border-fuchsia-200",
    description: "Trigger a chatbot workflow",
  },
  {
    type: "Update Chat Status",
    icon: <MessageCircle className="h-5 w-5" />,
    color: "bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200",
    description: "Update the status of the chat",
  },
  {
    type: "Template",
    icon: <FileBadge className="h-5 w-5" />,
    color: "bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200",
    description: "Use a predefined template",
  },
  {
    type: "Time Delay",
    icon: <Clock className="h-5 w-5" />,
    color: "bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200",
    description: "Add a time delay in the flow",
  },
  {
    type: "Webhook",
    icon: <Webhook className="h-5 w-5" />,
    color: "bg-gradient-to-br from-zinc-50 to-zinc-100 border-zinc-200",
    description: "Trigger an external webhook",
  },
  {
    type: "Google Spreadsheet",
    icon: <Table2 className="h-5 w-5" />,
    color: "bg-gradient-to-br from-green-50 to-green-100 border-green-200",
    description: "Interact with Google Spreadsheet",
  },
  {
    type: "Sets",
    icon: <Database className="h-5 w-5" />,
    color: "bg-gradient-to-br from-neutral-50 to-neutral-100 border-neutral-200",
    description: "Manage data sets",
  },
  {
    type: "Product",
    icon: <Package className="h-5 w-5" />,
    color: "bg-gradient-to-br from-stone-50 to-stone-100 border-stone-200",
    description: "Product-related actions",
  }
  // Other elements commented out as they are not part of the main element library
  // {
  //   type: "Greeting",
  //   icon: <MessageSquare className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200",
  //   description: "Start your conversation",
  // },
  // {
  //   type: "Open Question",
  //   icon: <HelpCircle className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-green-50 to-green-100 border-green-200",
  //   description: "Ask an open-ended question",
  // },
  // {
  //   type: "Closed Question",
  //   icon: <CheckSquare className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200",
  //   description: "Ask a yes/no question",
  // },
  // {
  //   type: "Information",
  //   icon: <Info className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200",
  //   description: "Provide information",
  // },
  // {
  //   type: "Condition",
  //   icon: <GitBranch className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-red-50 to-red-100 border-red-200",
  //   description: "Add a conditional branch",
  // },
  // {
  //   type: "Switch",
  //   icon: <RefreshCw className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200",
  //   description: "Create a switch statement",
  // },
  // {
  //   type: "Loop",
  //   icon: <Repeat className="h-5 w-5" />,
  //   color: "bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200",
  //   description: "Create a repeating sequence",
  // }
]

// Helper function to conditionally join class names
const cn = (...classes) => {
  return classes.filter(Boolean).join(" ")
}

// Custom edge component
const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, style = {}, markerEnd, data }) => {
  const [showDelete, setShowDelete] = useState(false);
  const edgePathRef = useRef(null);
  const { setEdges } = useReactFlow();

  const xEqual = sourceX === targetX;
  const yEqual = sourceY === targetY;
  const [edgeCenterX, edgeCenterY] = [
    xEqual ? sourceX : (sourceX + targetX) / 2,
    yEqual ? sourceY : (sourceY + targetY) / 2
  ];

  const onEdgeClick = useCallback((event) => {
    event.stopPropagation();
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  }, [id, setEdges]);

  const path = `M ${sourceX} ${sourceY} C ${sourceX} ${targetY} ${targetX} ${sourceY} ${targetX} ${targetY}`;

  return (
    <g
      className="react-flow__edge"
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      <path
        ref={edgePathRef}
        className="react-flow__edge-path"
        d={path}
        style={style}
        markerEnd={markerEnd}
      />      {showDelete && (
        <g transform={`translate(${edgeCenterX - 25} ${edgeCenterY - 12})`}>
          <foreignObject
            width="50"
            height="24"
            className="overflow-visible"
          >
            <button
              className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-md shadow-sm transition-colors flex items-center gap-1"
              onClick={onEdgeClick}
            >
              <X className="h-3 w-3" />
              Delete
            </button>
          </foreignObject>
        </g>
      )}
    </g>
  );
};

// Custom node component with glass morphism effect
const CustomNode = ({ data, selected }) => {
  // Add state to track editing choices for choices
  const [isEditingChoices, setIsEditingChoices] = useState(false);
  const [editableOptions, setEditableOptions] = useState(data.options || '');
  const [showMenu, setShowMenu] = useState(false);

  // Handle changes to the choices
  const handleOptionsChange = (event) => {
    setEditableOptions(event.target.value);
    // Update the node data
    if (data.updateOptions) {
      data.updateOptions(event.target.value);
    }
  };

  // Add a new choice
  const handleAddChoice = () => {
    const currentChoices = editableOptions.split('\n').filter(choice => choice.trim());
    const maxChoices = data.label === 'Button' ? 3 : 10;

    if (currentChoices.length < maxChoices) {
      const newChoice = `Choice ${currentChoices.length + 1}`;
      const updatedOptions = [...currentChoices, newChoice].join('\n');
      setEditableOptions(updatedOptions);
      data.updateOptions && data.updateOptions(updatedOptions);
    }
  };

  // Handle menu click
  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const getFilePreview = (type, file) => {
    switch (type) {
      case 'image':
        return (
          <div className="mt-2 relative">
            <img 
              src={URL.createObjectURL(file)} 
              alt={file.name} 
              className="w-full h-32 object-cover rounded-md"
            />
          </div>
        );
      case 'video':
        return (
          <div className="mt-2 relative">
            <video 
              src={URL.createObjectURL(file)} 
              controls 
              className="w-full rounded-md"
              style={{ maxHeight: '150px' }}
            />
          </div>
        );
      case 'audio':
        return (
          <div className="mt-2 relative">
            <audio 
              src={URL.createObjectURL(file)} 
              controls 
              className="w-full"
            />
          </div>
        );
      case 'document':
        const extension = file.name.split('.').pop().toLowerCase();
        return (
          <div className="mt-2 p-3 bg-gray-50 rounded-md flex items-center gap-2">
            <div className="p-2 rounded-full bg-blue-100">
              {extension === 'pdf' ? (
                <FileText className="h-4 w-4 text-blue-600" />
              ) : extension === 'doc' || extension === 'docx' ? (
                <File className="h-4 w-4 text-blue-600" />
              ) : (
                <FileText className="h-4 w-4 text-blue-600" />
              )}
            </div>
            <div className="flex-1 truncate text-xs">
              <div className="font-medium">{file.name}</div>
              <div className="text-gray-500">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (    
    <div className="relative">      
      <Handle 
        type="target" 
        position={Position.Left} 
        style={{
          background: data.label === "Button" ? "#22c55e" : "#555", 
          width: "10px", 
          height: "10px"
        }} 
      />
      {data.label !== "Button" && (
        <Handle 
          type="source" 
          position={Position.Right} 
          style={{
            background: "#555", 
            width: "10px", 
            height: "10px"
          }} 
        />
      )}
      <div
        className={cn(
          "p-4 rounded-lg shadow-lg backdrop-blur-sm border transition-all duration-200",
          data.color,
          selected ? "ring-2 ring-blue-500 ring-offset-2" : "",
          "min-w-[200px] max-w-[300px]",
        )}
      >        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div className="mr-3 p-2 rounded-full bg-white/80 shadow-sm">{data.icon}</div>
            <span className="font-semibold text-gray-800">{data.label}</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors relative"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-600">
              <circle cx="2" cy="8" r="1.5" fill="currentColor" />
              <circle cx="8" cy="8" r="1.5" fill="currentColor" />
              <circle cx="14" cy="8" r="1.5" fill="currentColor" />
            </svg>            {showMenu && (
              <div 
                className="absolute right-0 mt-1 py-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="w-full px-4 py-2 text-sm text-left text-blue-600 hover:bg-gray-100 flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (data.onEdit) data.onEdit();
                    setShowMenu(false);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
                <button
                  className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation(); 
                    if (data.onDuplicate) data.onDuplicate();
                    setShowMenu(false);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                  Copy
                </button>
                <button
                  className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-gray-100 flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (data.onDelete) data.onDelete();
                    setShowMenu(false);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </button>
        </div>
        <div className="mt-2 text-sm bg-white/80 p-3 rounded border border-gray-200 text-gray-700 break-words whitespace-pre-wrap min-h-[40px]">          
          <div className="font-medium mb-1">{data.content || "Add message here..."}</div>          
          {data.label === "Send a message" && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      e.stopPropagation();
                      const file = e.target.files[0];
                      if (file) {
                        if (!data.selectedFiles) data.selectedFiles = {};
                        data.selectedFiles.image = file;
                        data.updateFiles && data.updateFiles(data.selectedFiles);
                      }
                    }}
                    id={`image-upload-${data.id}`}
                  />
                  <button
                    className="text-xs px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded border border-blue-200 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(`image-upload-${data.id}`).click();
                    }}
                  >
                    + Image
                  </button>
                </div>

                <div className="relative">
                  <input
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => {
                      e.stopPropagation();
                      const file = e.target.files[0];
                      if (file) {
                        if (!data.selectedFiles) data.selectedFiles = {};
                        data.selectedFiles.audio = file;
                        data.updateFiles && data.updateFiles(data.selectedFiles);
                      }
                    }}
                    id={`audio-upload-${data.id}`}
                  />
                  <button
                    className="text-xs px-3 py-1 bg-violet-50 hover:bg-violet-100 text-violet-600 rounded border border-violet-200 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(`audio-upload-${data.id}`).click();
                    }}
                  >
                    + Audio
                  </button>
                </div>

                <div className="relative">
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      e.stopPropagation();
                      const file = e.target.files[0];
                      if (file) {
                        if (!data.selectedFiles) data.selectedFiles = {};
                        data.selectedFiles.video = file;
                        data.updateFiles && data.updateFiles(data.selectedFiles);
                      }
                    }}
                    id={`video-upload-${data.id}`}
                  />
                  <button
                    className="text-xs px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded border border-emerald-200 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(`video-upload-${data.id}`).click();
                    }}
                  >
                    + Video
                  </button>
                </div>

                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    className="hidden"
                    onChange={(e) => {
                      e.stopPropagation();
                      const file = e.target.files[0];
                      if (file) {
                        if (!data.selectedFiles) data.selectedFiles = {};
                        data.selectedFiles.document = file;
                        data.updateFiles && data.updateFiles(data.selectedFiles);
                      }
                    }}
                    id={`document-upload-${data.id}`}
                  />
                  <button
                    className="text-xs px-3 py-1 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded border border-amber-200 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(`document-upload-${data.id}`).click();
                    }}
                  >
                    + Document
                  </button>
                </div>
              </div>
              {data.selectedFiles && Object.keys(data.selectedFiles).length > 0 && (
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  {Object.entries(data.selectedFiles).map(([type, file]) => (
                    <div key={type} className="relative">
                      <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-1">
                          <span className="font-medium capitalize">{type}:</span> 
                          <span className="truncate max-w-[150px]">{file.name}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newFiles = { ...data.selectedFiles };
                            delete newFiles[type];
                            data.selectedFiles = newFiles;
                            data.updateFiles && data.updateFiles(newFiles);
                          }}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      {getFilePreview(type, file)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}          {data.options && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              {isEditingChoices ? (
                <div className="relative">
                  <textarea
                    value={editableOptions}
                    onChange={handleOptionsChange}
                    className="w-full p-2 border border-gray-300 rounded text-sm min-h-[100px]"
                    placeholder="Enter choices (one per line)"
                  />                  <div className="flex justify-between mt-2">
                    {((data.label === 'Button' && editableOptions.split('\n').filter(c => c.trim()).length < 3) ||
                      (data.label === 'List' && editableOptions.split('\n').filter(c => c.trim()).length < 10)) && (
                      <button
                        onClick={handleAddChoice}
                        className="text-xs px-3 py-1 bg-green-50 hover:bg-green-100 text-green-600 rounded border border-green-200 transition-colors"
                      >
                        Add Choice
                      </button>
                    )}
                    <button
                      onClick={() => setIsEditingChoices(false)}
                      className="text-xs px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded border border-blue-200 transition-colors"
                    >
                      Done Editing
                    </button>
                  </div>
                </div>
              ) : (
                <>                  {data.options.split('\n').map((option, index) => (
                    <div key={index} className="relative">
                      <div className={cn(
                        "mt-1 px-2 py-1 text-xs rounded flex items-center justify-between gap-2",
                        data.label === "Button" || data.label === "List"
                          ? "bg-green-50 border border-green-200 mb-3"
                          : "bg-gray-50 border border-gray-200"
                      )}>
                        <span className="flex-1">{option}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const choices = data.options.split('\n').filter(c => c.trim());
                              const newChoices = choices.filter((_, i) => i !== index);
                              data.updateOptions && data.updateOptions(newChoices.join('\n'));
                            }}
                            className="text-red-500 hover:text-red-600 p-1"
                            title="Delete choice"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          {(data.label === "Button" || data.label === "List") && (
                            <Handle
                              type="source"
                              position={Position.Right}
                              id={`${data.id}-option-${index}`}
                              style={{
                                background: "#22c55e",
                                width: "8px",
                                height: "8px",
                                right: "-20px"
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>))}
                  <div className="flex space-x-2 mt-2">
                    {((data.label === 'Button' && data.options.split('\n').filter(c => c.trim()).length < 3) ||
                      (data.label === 'List' && data.options.split('\n').filter(c => c.trim()).length < 10)) && (
                      <button
                        onClick={handleAddChoice}
                        className="text-xs px-3 py-1 bg-green-50 hover:bg-green-100 text-green-600 rounded border border-green-200 transition-colors"
                      >
                        Add Choice
                      </button>
                    )}
                    <button
                      onClick={() => setIsEditingChoices(true)}
                      className="text-xs px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded border border-blue-200 transition-colors"
                    >
                      Edit Choices
                    </button>
                  </div>
                </>
              )}
            </div>
          )}{(!data.options && data.answerType)&& (
            <div className="mt-2 text-xs text-gray-500">
              Expected: {data.answerType}
            </div>
          )}
        </div>
      </div>    </div>
  )
}

// Define node types
const nodeTypes = {
  custom: CustomNode,
}

// Define edge types
const edgeTypes = {
  custom: CustomEdge,
}

// Button component
const Button = ({ children, variant = "default", size = "md", onClick, disabled, className, title }) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"

  const variants = {
    default: "bg-blue-500 text-white hover:bg-blue-600",
    outline: "border border-gray-300 bg-white hover:bg-gray-50 text-gray-700",
    ghost: "hover:bg-gray-100 text-gray-700",
    destructive: "bg-red-500 text-white hover:bg-red-600",
  }

  const sizes = {
    sm: "h-8 px-3 text-xs",
    md: "h-10 px-4 text-sm",
    lg: "h-12 px-6 text-base",
    icon: "h-8 w-8",
  }

  return (
    <button
      className={cn(baseClasses, variants[variant], sizes[size], className)}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  )
}

// Tabs components
const Tabs = ({ children, defaultValue }) => {
  const [activeTab, setActiveTab] = useState(defaultValue)

  const childrenWithProps = React.Children.map(children, (child) => {
    if (child.type === TabsList || child.type === TabsContent) {
      return React.cloneElement(child, { activeTab, setActiveTab })
    }
    return child
  })

  return <div className="w-full">{childrenWithProps}</div>
}

const TabsList = ({ children, activeTab, setActiveTab }) => {
  const childrenWithProps = React.Children.map(children, (child) => {
    if (child.type === TabsTrigger) {
      return React.cloneElement(child, { activeTab, setActiveTab })
    }
    return child
  })

  return <div className="flex border-b border-gray-200 mb-4">{childrenWithProps}</div>
}

const TabsTrigger = ({ children, value, activeTab, setActiveTab, className }) => {
  return (
    <button
      className={cn(
        "flex-1 py-2 px-4 text-sm font-medium transition-colors",
        activeTab === value ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700",
        className,
      )}
      onClick={() => setActiveTab(value)}
    >
      {children}
    </button>
  )
}

const TabsContent = ({ children, value, activeTab }) => {
  if (value !== activeTab) return null
  return <div>{children}</div>
}

// Input and Textarea components
const Input = ({ value, onChange, className, ...props }) => {
  return (
    <input
      value={value}
      onChange={onChange}
      className={cn(
        "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
        className,
      )}
      {...props}
    />
  )
}

const Textarea = ({ value, onChange, className, rows = 3, ...props }) => {
  return (
    <textarea
      value={value}
      onChange={onChange}
      rows={rows}
      className={cn(
        "flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
        className,
      )}
      {...props}
    />
  )
}

const ConversationFlowDesigner = ({ onSave, initialFlow = { nodes: [], edges: [] } }) => {
  const safeFlow = initialFlow ?? { nodes: [], edges: [] }

  const [nodes, setNodes, onNodesChange] = useNodesState(safeFlow.nodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(safeFlow.edges)
  const [openElements, setOpenElements] = useState({}) // Track which elements are open
  const [selectedElement, setSelectedElement] = useState(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [suggestionPosition, setSuggestionPosition] = useState({ x: 0, y: 0 })
  const [sourceNodeId, setSourceNodeId] = useState(null)
  const [history, setHistory] = useState({
    past: [],
    present: { nodes: safeFlow.nodes, edges: safeFlow.edges },
    future: [],
  })  
  const [isUndoRedo, setIsUndoRedo] = useState(false)
  const [isMinimized, setIsMinimized] = useState(false)
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 })

  // ReactFlow instance
  const reactFlowInstance = useReactFlow()
  const reactFlowWrapper = useRef(null)
  // Save current state to history
  const saveToHistory = useCallback(() => {
    if (isUndoRedo) {
      setIsUndoRedo(false)
      return
    }
    setHistory((prev) => ({
      past: [...prev.past, prev.present],
      present: { nodes, edges },
      future: [],
    }))
  }, [nodes, edges, isUndoRedo])

  // Handle undo
  const handleUndo = useCallback(() => {
    if (history.past.length === 0) return
    setIsUndoRedo(true)
    const newPresent = history.past[history.past.length - 1]
    const newPast = history.past.slice(0, history.past.length - 1)
    setNodes(newPresent.nodes)
    setEdges(newPresent.edges)
    setHistory({
      past: newPast,
      present: newPresent,
      future: [history.present, ...history.future],
    })
  }, [history, setNodes, setEdges])

  // Handle redo
  const handleRedo = useCallback(() => {
    if (history.future.length === 0) return
    setIsUndoRedo(true)
    const newPresent = history.future[0]
    const newFuture = history.future.slice(1)
    setNodes(newPresent.nodes)
    setEdges(newPresent.edges)
    setHistory({
      past: [...history.past, history.present],
      present: newPresent,
      future: newFuture,
    })
  }, [history, setNodes, setEdges])

  // Handle node options changes - Define this first
  const handleNodeOptionsChange = useCallback((nodeId, newOptions) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          const options = newOptions.split('\n');
          const maxOptions = node.data.label === 'Button' ? 3 : node.data.label === 'List' ? 10 : Infinity;
          const limitedOptions = options.slice(0, maxOptions).join('\n');
          
          return {
            ...node,
            data: {
              ...node.data,
              options: limitedOptions,
            },
          };
        }
        return node;
      })
    );
  }, [setNodes]);
  
  // Handle file updates - Define this before addElement
  const handleFileUpdate = useCallback((nodeId, selectedFiles) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                selectedFiles,
              },
            }
          : node
      )
    )
    setTimeout(saveToHistory, 0)
  }, [setNodes, saveToHistory]);

  // The main addElement function - single implementation
  const addElement = useCallback(
    (type, customPosition = null) => {
      // First try to find it in the main types
      let elementType = elementTypes.find((el) => el.type === type);
      let icon = null;
      let color = "";
      let questionType = null;
      
      // If not found in main types, search in subtypes
      if (!elementType) {
        for (const mainType of elementTypes) {
          if (mainType.subTypes) {
            const subType = mainType.subTypes.find(sub => sub.type === type);
            if (subType) {
              icon = subType.icon;
              color = mainType.color;
              questionType = subType.questionType;
              break;
            }
          }
        }
      } else {
        icon = elementType.icon;
        color = elementType.color;
      }

      // If still not found, return to prevent error
      if (!icon || !color) {
        console.error(`Element type "${type}" not found`);
        return;
      }

      const position = customPosition || reactFlowInstance.project({
        x: Math.random() * 300 + 50,
        y: Math.random() * 300 + 50,
      });

      const nodeId = `${type}-${Date.now()}`;
      const clonedIcon = icon ? React.cloneElement(icon, { key: Date.now() }) : null;

      const newNode = {
        id: nodeId,
        type: "custom",
        position,
        data: {
          label: type,
          icon: clonedIcon,
          color: color,
          content: "",
          questionType: questionType,
          options: type === "Button" ? "Choice 1\nChoice 2" : 
                   type === "List" ? "Option 1\nOption 2\nOption 3" : "",
          updateOptions: (newOptions) => handleNodeOptionsChange(nodeId, newOptions),
          selectedFiles: {},
          updateFiles: (files) => handleFileUpdate(nodeId, files),
          onEdit: () => setSelectedElement({ id: nodeId, type: "custom", data: { label: type } }),
          onDuplicate: () => {
            const duplicatePosition = {
              x: position.x + 50,
              y: position.y + 50
            };
            addElement(type, duplicatePosition);
          },
          onDelete: () => {
            setNodes((nds) => nds.filter((n) => n.id !== nodeId));
            setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
          },
        },
      };
      
      setNodes((nds) => [...nds, newNode]);
      setTimeout(saveToHistory, 0);
      return newNode;
    },
    [reactFlowInstance, setNodes, setEdges, handleNodeOptionsChange, handleFileUpdate, setSelectedElement, saveToHistory]
  );

  // Handle suggestion click
  const handleSuggestionClick = useCallback((type) => {
    if (sourceNodeId && suggestionPosition) {
      const newNode = addElement(type, suggestionPosition);
      if (newNode) {
        setEdges((eds) => {
          const newEdges = addEdge(
            {
              source: sourceNodeId,
              target: newNode.id,
              animated: true,
              style: { stroke: "#555", strokeWidth: 2 },
            },
            eds
          );
          return newEdges;
        });
      }
    }
    setShowSuggestions(false);
    setSourceNodeId(null);
  }, [sourceNodeId, suggestionPosition, addElement, setEdges]);

  // Connect nodes
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        const newEdges = addEdge(          {
            ...params,
            type: 'custom',
            animated: true,
            style: { stroke: "#555", strokeWidth: 2 },
          },
          eds,
        );
        return newEdges;
      });
      setTimeout(saveToHistory, 0);
    },
    [setEdges, saveToHistory]
  );

  // Handle connection start
  const onConnectStart = useCallback((_, { nodeId }) => {
    setSourceNodeId(nodeId);
  }, []);

  // Handle connection end
  const onConnectEnd = useCallback((event) => {
    const targetIsPane = event.target.classList.contains('react-flow__pane');
    
    if (targetIsPane && sourceNodeId) {
      // Get the position where the connection was dropped
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });
      
      setShowSuggestions(true);
      setSuggestionPosition(position);
    } else {
      setShowSuggestions(false);
    }
  }, [sourceNodeId, reactFlowInstance]);

  // Update element properties
  const updateElement = useCallback(
    (id, updates) => {
      const updatedNodes = nodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, ...updates } } : node
      )
      setNodes(updatedNodes)
      if (selectedElement && selectedElement.id === id) {
        setSelectedElement({ ...selectedElement, data: { ...selectedElement.data, ...updates } })
      }
      setTimeout(saveToHistory, 0)
    },
    [nodes, selectedElement, setNodes, saveToHistory],
  )

  // Handle node selection with safety check
  const onNodeClick = useCallback((_, node) => {
    if (node && node.data) {
      setSelectedElement(node);
    }
  }, [])

  // Handle background click to deselect
  const onPaneClick = useCallback(() => {
    setSelectedElement(null)
  }, [])

  // Handle node changes
  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes)
      setTimeout(saveToHistory, 100)
    },
    [onNodesChange, saveToHistory],
  )

  // Handle edge changes
  const handleEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes)
      setTimeout(saveToHistory, 100)
    },
    [onEdgesChange, saveToHistory],
  )

  // Delete selected node
  const deleteSelectedNode = useCallback(() => {
    if (!selectedElement) return
    setNodes((nds) => nds.filter((n) => n.id !== selectedElement.id))
    setEdges((eds) =>
      eds.filter((e) => e.source !== selectedElement.id && e.target !== selectedElement.id),
    )
    setSelectedElement(null)
    setTimeout(saveToHistory, 0)
  }, [selectedElement, setNodes, setEdges, saveToHistory])

  // Save the flow
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave({ nodes, edges })
    }
  }, [nodes, edges, onSave])

  // Fit view to see all nodes
  const fitView = useCallback(() => {
    reactFlowInstance.fitView({ padding: 0.2 })
  }, [reactFlowInstance])

  const toggleElement = useCallback((type) => {
    setOpenElements((prev) => ({
      ...prev,
      [type]: !prev[type],
    }))
  }, [])

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      let newNode = {
        id: getNodeId(),
        type: 'custom',
        position,
        data: { 
          label: type,
          options: type === "Button" 
            ? "Answer 1\nDefault" 
            : type === "List"
            ? "Option 1\nOption 2\nOption 3"
            : ""
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance]
  );
  return (
    <div className="flex h-full bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
      {/* Element Library Panel */}
      <div className="w-72 bg-white/80 backdrop-blur-sm p-5 shadow-lg overflow-y-auto border-r border-gray-200">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Element Library</h3>
        <div className="space-y-2">
          {elementTypes.map((el) => (
            <div key={el.type}>
              <motion.div
                className={cn(
                  "flex items-center p-3 rounded-lg cursor-pointer border shadow-sm transition-all",
                  el.color,
                )}
                onClick={() => el.subTypes ? toggleElement(el.type) : addElement(el.type)}
                whileHover={{ scale: 1.02, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="bg-white p-2 rounded-full mr-3 shadow-sm">{el.icon}</div>
                <div className="flex-1">
                  <div className="font-medium">{el.type}</div>
                  <div className="text-xs text-gray-500">{el.description}</div>
                </div>
                {el.subTypes && (
                  <div className={cn(
                    "transition-transform",
                    openElements[el.type] ? "rotate-180" : ""
                  )}>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                )}
              </motion.div>
              
              {el.subTypes && (
                <AnimatePresence>
                  {openElements[el.type] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 space-y-2"
                    >
                      {el.subTypes.map((subType) => (
                        <motion.div
                          key={subType.type}
                          className={cn(
                            "flex items-center p-2 rounded-lg cursor-pointer border shadow-sm transition-all",
                            el.color,
                          )}
                          onClick={() => addElement(subType.type)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="bg-white p-1.5 rounded-full mr-2 shadow-sm">{subType.icon}</div>
                          <div>
                            <div className="font-medium text-sm">{subType.type}</div>
                            <div className="text-xs text-gray-500">{subType.description}</div>
                          </div>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Flow Editor */}
      <div className="flex-1 flex flex-col">
        <div className="p-4 border-b border-gray-200 bg-white/80 backdrop-blur-sm flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Conversation Flow Designer</h2>
            <p className="text-gray-600 text-sm">
              Create a custom interaction flow by dragging elements and connecting them
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={handleUndo} disabled={history.past.length === 0}>
              <Undo2 className="h-4 w-4 mr-1" /> Undo
            </Button>
            <Button variant="outline" size="sm" onClick={handleRedo} disabled={history.future.length === 0}>
              <Redo2 className="h-4 w-4 mr-1" /> Redo
            </Button>
            <Button variant="default" size="sm" onClick={handleSave}>
              <Save className="h-4 w-4 mr-1" /> Save Flow
            </Button>
          </div>
        </div>

        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            snapToGrid
            snapGrid={[15, 15]}            defaultEdgeOptions={{
              type: 'custom',
              animated: true,
              style: { stroke: "#555", strokeWidth: 2 },
            }}
            proOptions={{ hideAttribution: true }}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <Controls />
            <Background color="#aaa" gap={16} variant="dots" />
            <MiniMap
              nodeStrokeWidth={3}
              zoomable
              pannable
              nodeColor={(n) => {
                const type = n.data?.label || "default"
                const elementType = elementTypes.find((el) => el.type === type)
                return elementType?.color.split(" ")[1] || "#eee"
              }}
              maskColor="rgba(240, 240, 240, 0.6)"
            />

            <Panel
              position="top-right"
              className="flex space-x-2 bg-white/80 backdrop-blur-sm p-2 rounded-lg shadow-sm border border-gray-200"
            >
              <Button variant="outline" size="icon" onClick={fitView} title="Fit View">
                <Maximize className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => reactFlowInstance.zoomIn()} title="Zoom In">
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => reactFlowInstance.zoomOut()} title="Zoom Out">
                <ZoomOut className="h-4 w-4" />
              </Button>
            </Panel>
          </ReactFlow>
        </div>
      </div>      {/* Add Suggestions Popup */}
      <AnimatePresence>
        {showSuggestions && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}              transition={{ type: "spring", damping: 20 }}            
            drag
            dragMomentum={false}
            dragConstraints=".react-flow__viewport"
            dragElastic={0.2}
            className="absolute z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-72"
            style={{
              left: suggestionPosition.x,
              top: suggestionPosition.y,
              maxHeight: "400px",
              overflow: "hidden",
              cursor: "move",
              position: "absolute",
              transform: "translate(-50%, -50%)"
            }}
          >
            <div className="flex justify-between items-center mb-2 select-none">
              <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-gray-400" />
                Suggested Elements
              </div>
              <button
                onClick={() => {
                  setShowSuggestions(false);
                  setSourceNodeId(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-2 overflow-y-auto max-h-[320px] pr-2">              {elementTypes.map((el) => (
                <div key={el.type}>
                  {el.type === "Ask a Question" ? (
                    <div className="space-y-2">
                      {el.subTypes.map((subType) => (
                        <motion.div
                          key={subType.type}
                          className={cn(
                            "flex items-center p-2 rounded-lg cursor-pointer border shadow-sm transition-all",
                            el.color,
                          )}
                          onClick={() => handleSuggestionClick(subType.type)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="bg-white p-1.5 rounded-full mr-2 shadow-sm">{subType.icon}</div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{subType.type}</div>
                            <div className="text-xs text-gray-500">{subType.description}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <motion.div
                      className={cn(
                        "flex items-center p-2 rounded-lg cursor-pointer border shadow-sm transition-all",
                        el.color,
                      )}
                      onClick={() => handleSuggestionClick(el.type)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="bg-white p-1.5 rounded-full mr-2 shadow-sm">
                        {el.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{el.type}</div>
                        <div className="text-xs text-gray-500">{el.description}</div>
                      </div>
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Properties Panel */}
      <AnimatePresence>
        {selectedElement && selectedElement.data && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              height: isMinimized ? 'auto' : undefined,
              width: isMinimized ? '200px' : '320px'
            }}
            exit={{ opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            drag
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragEnd={(event, info) => {
              setPanelPosition(info.point);
            }}
            className="fixed top-24 right-4 bg-white/95 backdrop-blur-sm p-5 shadow-lg overflow-y-auto border border-gray-200 rounded-lg"
            style={{ 
              maxHeight: isMinimized ? '60px' : 'calc(100vh - 120px)', 
              zIndex: 1000,
              x: panelPosition.x,
              y: panelPosition.y
            }}
          >
            <div className="flex justify-between items-center mb-4 cursor-move" style={{ touchAction: 'none' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-800">Properties</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => setIsMinimized(!isMinimized)}
                >
                  {isMinimized ? (
                    <Maximize2 className="h-4 w-4" />
                  ) : (
                    <Minimize2 className="h-4 w-4" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setSelectedElement(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {!isMinimized && (
              <Tabs defaultValue="content">
                <TabsList className="mb-4">
                  <TabsTrigger value="content" className="flex-1">
                    Content
                  </TabsTrigger>
                  <TabsTrigger value="style" className="flex-1">
                    Style
                  </TabsTrigger>
                  <TabsTrigger value="actions" className="flex-1">
                    Actions
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="content" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Node Label</label>
                    <Input
                      value={selectedElement.data.label}
                      onChange={(e) => updateElement(selectedElement.id, { label: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <Textarea
                      rows={5}
                      value={selectedElement.data.content}
                      onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                      placeholder="Enter node content..."
                      className="resize-none"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="style" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Node Type</label>
                    <select
                      className="w-full rounded-md border border-gray-300 p-2 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      value={selectedElement.data.label}
                      onChange={(e) => {
                        const type = e.target.value
                        const elementType = elementTypes.find((el) => el.type === type)
                        updateElement(selectedElement.id, {
                          label: type,
                          icon: elementType.icon,
                          color: elementType.color,
                        })
                      }}
                    >
                      {elementTypes.map((el) => (
                        <option key={el.type} value={el.type}>
                          {el.type}
                        </option>
                      ))}
                    </select>
                  </div>
                </TabsContent>

                <TabsContent value="actions" className="space-y-4">
                  <Button variant="destructive" className="w-full" onClick={deleteSelectedNode}>
                    <Trash2 className="h-4 w-4 mr-2" /> Delete Node
                  </Button>
                </TabsContent>
              </Tabs>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function ConversationFlowApp() {
  const [savedFlow, setSavedFlow] = useState(null)

  const handleSave = (flow) => {
    setSavedFlow(flow)
    // In a real app, you might want to save this to a database or localStorage
    console.log("Flow saved:", flow)
  }

  return (
    <div className="h-screen flex flex-col">
      <ReactFlowProvider>
        <ConversationFlowDesigner onSave={handleSave} initialFlow={savedFlow} />
      </ReactFlowProvider>
    </div>
  )
}

export default ConversationFlowApp